import { useState, useRef } from "react";
import { Button, StatBlock, DialogCancel } from "@cntrl/ui";
import { Modal } from "../../releases/Modal.js";
import { formErrorMessage } from "../../../app/hooks/apiError.js";
import { useImportFans, type FanImportResult } from "../../../app/hooks/useFans.js";
import styles from "./FanForm.module.scss";

/**
 * The imports modal — brings contacts INTO the fan list from a CSV. Idempotent server-side
 * (ux_fans_band_email), so a re-run never duplicates; the result shows imported / updated / skipped.
 *
 * CSV is the only import source, so there is no source picker — a toggle with one option is worse than none.
 * There is deliberately NO Shopify-customer import (DROP-01 / App-Review least-privilege). The file needs an
 * `email` column header.
 */
export function ImportFansModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const importFans = useImportFans();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FanImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function run() {
    setError(null);
    if (!file) {
      setError("Choose a CSV file to import.");
      return;
    }
    importFans.mutate(
      { file },
      {
        onSuccess: (r) => {
          setResult(r);
          onImported(); // invalidates the fan directory; the modal stays to show the result
        },
        onError: (err) => setError(formErrorMessage(err, "Couldn't import your fans")),
      },
    );
  }

  if (result) {
    return (
      <Modal title="Import complete" size="md" onClose={onClose}>
        <div className={styles.form}>
          <div className={styles.importResult}>
            <StatBlock label="Added" value={result.imported ?? 0} />
            <StatBlock label="Updated" value={result.updated ?? 0} />
            <StatBlock label="Skipped" value={result.skipped ?? 0} />
          </div>
          {result.truncated && (
            <p className={styles.note}>
              Your audience was larger than one import — run it again to bring in the rest.
            </p>
          )}
          <p className={styles.note}>Skipped rows had no email address, or were duplicates already in your list.</p>
          <div className={styles.actionsEnd}>
            <Button type="button" variant="primary" onClick={onClose}>Done</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Import fans" size="md" onClose={onClose}>
      <div className={styles.form}>
        <label className={styles.row}>
          <span className={styles.label}>CSV file</span>
          <input
            ref={fileRef}
            className={styles.fileInput}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => { setFile(e.currentTarget.files?.[0] ?? null); setError(null); }}
          />
          <span className={styles.note}>Headers: email (required), first name, last name, phone, city, state, country.</span>
        </label>

        {error && <p className={styles.formError} role="alert">{error}</p>}
        <div className={styles.actionsEnd}>
          <DialogCancel />
          <Button
            type="button"
            variant="primary"
            loading={importFans.isPending}
            onClick={run}
          >
            Import
          </Button>
        </div>
      </div>
    </Modal>
  );
}
